/**
 * Every user-facing string for the DFC Fitness Village journey, in one place — like
 * `schoolExperienceCopy.ts`. Components never carry copy of their own: they read it with
 * `useFitnessVillageCopy()`, which returns this object or its Arabic twin
 * (`fitnessVillageCopy.ar.ts`, typed by `FitnessVillageCopy` so the two cannot drift).
 *
 * Strings marked UNVERIFIED are DFC's / its partners' claims lifted from the design canvas
 * (counts, dates, "free", "one booking per person") and need DFC's confirmation before launch.
 */

export const FITNESS_VILLAGE_COPY_EN = {
  home: {
    /** Challenge dates, per DFC's 26/09 feedback */
    dateline: '31 October – 29 November 2026',
    /** The headline, on two lines (18/10 mock) */
    heroTitle: { lead: 'Dubai Fitness', tail: 'Challenge' },
    /** "What's your 30x30?", the line beside the headline (desktop) or under it (mobile) */
    heroLead: "What's your",
    heroAccent: '30x30?',
    /** Alt text of the hero visual (`DFC_HERO_ARTWORK`) that stands in for that line */
    heroArtworkAlt: '30 minutes, 30 days',
    /** DFC 14/10. Keep it to ~160 characters: two lines beside the stats panel on desktop. */
    intro:
      'Join the city in committing to 30 minutes of activity for 30 days from 31 October to 29 November.',
    chooseVillage: 'Choose a village',
    classesAction: {
      label: 'Classes',
      sub: 'No booking needed',
      /** aria-label of the card action: "Browse classes at Kite Beach" */
      aria: (village: string) => `Browse classes at ${village}`,
    },
    courtsAction: {
      label: 'Courts',
      sub: 'Book a slot',
      /** aria-label of the card action: "Book a court at Kite Beach" */
      aria: (village: string) => `Book a court at ${village}`,
    },
    /** Mobile card disclosure — one village is open at a time. */
    expandVillage: (village: string) => `Show ${village} options`,
    collapseVillage: (village: string) => `Hide ${village} options`,
    /** The company's catalogue carries no village services yet. */
    emptyVillages: 'Villages open for booking soon.',
    stats: {
      title: 'The challenge in numbers',
      /** Removed (DFC 14/10); an empty footnote is not drawn. */
      footnote: '',
    },
    lockups: {
      title: 'The Fitness Villages',
      /** alt text of a lockup: "Kite Beach Fitness Village" */
      alt: (village: string) => `${village} Fitness Village`,
    },
  },

  /** UNVERIFIED — every figure here is DFC's to confirm. */
  homeStats: [
    { label: 'Fitness Villages', value: '3' },
    { label: 'Zones across the 30x30 Fitness Villages', value: '24' },
    { label: 'Days of free movement', value: '30' },
  ],

  page: {
    allVillages: 'All villages',
    /** Accessible name of the in-page village switcher */
    switchVillage: 'Switch village',
    /** The whole classes heading (DET 20/10): the village chips above already name the village. */
    classesLead: "What's on",
    courtsLead: 'Book a court at',
    tabs: { classes: 'Classes', courts: 'Courts', ariaLabel: 'Classes or courts' },
    zones: 'Zones',
    /** The courts tab's picker label */
    sports: 'Courts',
    close: 'Close',
    alsoOnToday: 'Also on today',
    alsoBookable: 'Also bookable',
    seeAllZones: (count: number) => `See all ${count} zones`,
    seeAllSports: (count: number) => `See all ${count} sports`,
    timetable: 'Timetable',
    yourDay: 'Your day',
    happeningNow: 'Happening now',
    howLong: 'How long?',
    sessionLength: 'Session length',
    start: 'Start',
    bookCourt: 'Book',
    /** The hero line under "What's on at <village>" */
    classesIntro:
      'Free to attend with no pre-booking required. Participation is subject to available capacity in each zone.',
    /** UNVERIFIED — 60 minutes and one per group per day wait on JAMES's court slot numbers */
    courtsIntro:
      "Book a court for 60 minutes. One booking per group per day. We'd love as many participants as possible to enjoy the facilities, so please keep it fair!",
    /** The Courts tab's hours line, once court timings are set: "Court bookings: Daily 16:00 – 22:00" */
    courtHours: (hours: string) => `Court bookings: ${hours}`,
    /** The note under the class timetable */
    classesNote: 'Classes are subject to availability and may fill up.',
    chooseDay: 'Choose a day',
    /** The day strip's label for today, in place of the weekday */
    today: 'Today',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    emptyClasses: 'No classes scheduled here today.',
    /** A drop-in zone (no sessions in the event): the label above its message */
    dropInLabel: 'Drop in',
    /** A drop-in zone whose service has no description in the admin panel */
    dropInFallback: 'Drop in during opening hours, no booking needed.',
    emptySlots: 'No free slots left for this day.',
    loadError: 'We could not load this village. Try again.',
    retry: 'Try again',
    mapComingSoon: 'Village map coming soon.',
    /** "Kite Beach Fitness Village" — the lockup band's eyebrow and alt text */
    villageTitle: (village: string) => `${village} Fitness Village`,
    /** Section headings of the timetable and the slot grid, keyed by `PeriodOfDay` */
    periods: { Morning: 'Morning', Afternoon: 'Afternoon', Evening: 'Evening' },
    /** "4 courts" — appended to a sport in the courts picker */
    courtsCount: (count: number) => (count === 1 ? '1 court' : `${count} courts`),
    /** aria-label of the wide "currently selected" card */
    changeSelection: (label: string) => `Change selection. Currently ${label}`,
    /** aria-label of a taken court slot */
    alreadyBooked: (time: string) => `${time} — already booked`,
    /** aria-label of a period's group of slots: "Evening slots" */
    periodSlots: (period: string) => `${period} slots`,
    /** Shown when the URL names a village the company does not have */
    unknownVillage: 'We could not find this village.',
    /** Timeline marker shown on a free session */
    free: 'Free',
    /** A class row's details — DFC 12/10: capacity and description must be visible */
    classDetails: {
      /** "30 min" */
      minutes: (count: number) => `${count} min`,
      /** "Capacity 150" */
      capacity: (count: number) => `Capacity ${count}`,
      /** When the session shows remaining capacity: "12 of 30 spots left" */
      spotsLeft: (left: number, total: number) => `${left} of ${total} spots left`,
      full: 'Full',
      dropIn: 'Free drop-in — no booking needed. Capacity limits apply.',
      noDescription: 'More about this class soon.',
    },
    reserveError: 'We could not book this slot. Try another time.',
  },

  auth: {
    eyebrow: 'Dubai Fitness Challenge',
    signIn: {
      tab: 'Sign in',
      title: 'Welcome back',
      body: 'Sign in to see your bookings.',
      cta: 'Sign in',
    },
    signUp: {
      tab: 'Create account',
      title: 'Create account',
      body: 'One account for every village — keep your court bookings in one place.',
      cta: 'Create account',
    },
    /** Accessible name of the sign in / create account overlay */
    overlayLabel: 'Sign in or create an account',
    email: 'Email',
    emailPlaceholder: 'you@email.com',
    password: 'Password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    forgotPassword: 'Forgot password?',
    /** The forgot-password link once the reset email has gone */
    resetSent: 'Reset email sent',
    pending: 'One moment…',
    orContinueWith: 'or continue with',
    google: 'Google',
    apple: 'Apple',
    close: 'Close',
  },

  header: {
    logoAlt: 'Dubai Fitness Challenge',
    home: 'Dubai Fitness Challenge home',
    mainNav: 'Main',
    signIn: 'Sign in',
    openMenu: 'Open menu',
    account: 'Account',
    quickCheckIn: 'Quick check-in QR code',
    cart: 'Cart',
    /** Accessible name of the light / dark switch (role="switch", checked = dark) */
    darkMode: 'Dark mode',
    /** The shared quick check-in QR modal, opened from the account button */
    qrModal: {
      title: 'Quick Check-in',
      subtitle: 'Scan QR Code for quick Check-in',
      viewProfile: 'View My Profile',
      close: 'Close',
    },
    /**
     * The language switch shows the language it switches *to*, written in that language
     * ("العربية" on the English site), so a reader of either can find it.
     */
    languageSwitch: { label: 'العربية', short: 'ع', lang: 'ar', aria: 'Switch to Arabic' },
    /** The canvas's nav, used when the company has no `sidebarContent` of its own */
    nav: {
      home: 'Home',
      schedule: 'Classes',
      bookings: 'Courts',
    },
    /** The mobile menu drawer */
    menu: 'Menu',
    closeMenu: 'Close menu',
    myProfile: 'My profile',
    logOut: 'Log out',
    menuFootnote: 'Dubai Fitness Challenge · 31 October – 29 November 2026',
    /**
     * Prefix on the cart countdown strip: "Complete your booking in 09:42". DFC copy says "book",
     * never "hold" / "reserve"; the key keeps the timer's name, not its wording.
     */
    cartHold: 'Complete your booking in',
    /** aria-label of the same strip: "Complete your booking in 9 minutes 42 seconds". */
    cartHoldAria: (minutes: number, seconds: number) =>
      `Complete your booking in ${minutes} minute${minutes === 1 ? '' : 's'} ${seconds} second${
        seconds === 1 ? '' : 's'
      }`,
  },

  /** Cart, checkout and add-ons — the steps between picking a slot and the confirmation. */
  checkout: {
    backToVillages: 'All villages',
    heroLead: 'Your',
    heroAccent: 'booking',
    cartHeroAccent: 'cart',
    addOnsHeroLead: 'Add',
    addOnsHeroAccent: 'extras',
    /** Section labels */
    bookings: 'Your bookings',
    addOns: 'Add-ons',
    optional: 'Optional',
    discounts: 'Discounts',
    details: 'Your details',
    summary: 'Summary',
    confirm: 'Confirm',
    /** "2 items" beside the bookings label */
    itemCount: (count: number) => (count === 1 ? '1 item' : `${count} items`),
    /** Booking row */
    remove: 'Remove',
    cancel: 'Cancel',
    /** aria-label of a row's remove action: "Remove Padel at 18:30" */
    removeAria: (service: string, time: string) => `Remove ${service} at ${time}`,
    cancelAria: (service: string, time: string) => `Cancel ${service} at ${time}`,
    minutes: (count: number) => `${count} min`,
    quantity: (count: number) => `×${count}`,
    addOnsCount: (count: number) => `Add-ons (${count})`,
    invoice: 'Generate invoice',
    share: 'Share',
    free: 'Free',
    emptyCart: 'Your cart is empty.',
    emptyCartAction: 'Browse villages',
    /** Shown on checkout between a successful free booking and the confirmation page */
    handoffTitle: 'Booking confirmed',
    handoffBody: 'Opening your confirmation…',
    noBookings: 'No bookings found.',
    /** Pricing */
    subtotal: 'Subtotal',
    discount: 'Discount',
    vat: 'VAT',
    total: 'Total',
    paid: 'Paid',
    /** Guest details */
    detailsIntro: 'We send your booking details to this email.',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Mobile number',
    country: 'Country code',
    /** Terms and the confirm action */
    agreeLead: 'I agree to the',
    privacy: 'privacy policy',
    terms: 'terms & conditions',
    cancellationPolicy: 'cancellation policy',
    and: 'and',
    /** Punctuation of the agreement sentence — Arabic has its own comma */
    listSeparator: ', ',
    sentenceEnd: '.',
    cancellationNote: (hours: number) =>
      `Bookings can be cancelled online up to ${hours} hours before they start.`,
    yes: 'Yes',
    no: 'No',
    processing: 'One moment…',
    actions: {
      book: 'Book now',
      pay: 'Continue to payment',
      package: 'Book with package',
    },
    /** Checkout sign-in, for a company that needs (or offers) an account */
    signInInstead: 'Have an account? Sign in',
    guestInstead: 'Continue as a guest',
    continueToCheckout: 'Continue to checkout',
    continue: 'Continue',
    addOnsNote: 'Add equipment to your court booking, or continue without.',
    noAddOns: 'No add-ons for this booking.',
    addOnPer: (price: string) => `${price} each`,
    decrease: (name: string) => `One less ${name}`,
    increase: (name: string) => `One more ${name}`,
  },

  confirmation: {
    eyebrow: "You're booked",
    heroLead: 'Booking',
    heroAccent: 'confirmed',
    /** Lead line, with and without the email the cart was booked under */
    sentTo: (email: string) => `We've sent the details to ${email}.`,
    sent: "We've sent the details to your email.",
    booked: 'Booked',
    backHome: 'Back to villages',
  },

  /** My profile — details, bookings, memberships and credits, and the account actions. */
  profile: {
    tabs: {
      ariaLabel: 'Profile sections',
      profile: 'Profile',
      upcoming: 'Upcoming',
      previous: 'Previous',
    },
    /** Shown in the avatar when the account has no photo and no name */
    avatarFallback: '?',
    details: 'Your details',
    fullName: 'Full name',
    email: 'Email',
    mobile: 'Mobile',
    dateOfBirth: 'Date of birth',
    nationality: 'Nationality',
    empty: '—',
    checkIn: 'Quick check-in',
    checkInBody: 'Show this code at the village entrance.',
    wallet: 'Add to your wallet',
    memberships: 'Memberships',
    credits: 'Vouchers & credits',
    noMemberships: 'No memberships yet.',
    noCredits: 'No vouchers or credits yet.',
    active: 'Active',
    inactive: 'Inactive',
    code: 'Code',
    remaining: 'Remaining',
    value: 'Value',
    redeemed: 'Redeemed',
    price: 'Price',
    purchased: 'Purchased',
    starts: 'Starts',
    validUntil: 'Valid until',
    /** Title of a credit voucher with no name of its own */
    creditFallback: 'Credit from a cancelled booking',
    invoice: 'Generate invoice',
    noUpcoming: 'No upcoming bookings.',
    noPrevious: 'No previous bookings.',
    browseVillages: 'Browse villages',
    /** "Order placed on 27 September 2026" */
    orderPlaced: (date: string) => `Order placed on ${date}`,
    order: 'Order',
    downloadInvoice: 'Download invoice',
    /** "1–3 of 7" */
    pageRange: (from: number, to: number, total: number) => `${from}–${to} of ${total}`,
    previousPage: 'Previous page',
    nextPage: 'Next page',
    account: 'Account',
    logOut: 'Log out',
    deleteAccount: 'Delete account',
    deleteTitle: 'Delete your account?',
    deleteBody:
      'This permanently deletes your account and everything linked to it, including your bookings.',
    deleteConfirm: 'Delete account',
    cancel: 'Cancel',
  },

  /** The second step of "Create account" (after the email) — CompleteProfile in the village design. */
  signup: {
    eyebrow: 'Dubai Fitness Challenge',
    titleEmail: 'Create your account',
    titleSocial: 'Complete your profile',
    bodyEmail: 'Choose a password to finish creating your account.',
    bodySocial: 'A couple of details and you are all set.',
    firstName: 'First name',
    lastName: 'Last name',
    password: 'Password',
    passwordConfirm: 'Re-enter password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    /** DFC's legal wording, unchanged from the generic form's DFC branch */
    consentLead: 'By creating an account, I confirm that I agree to the',
    privacyNotice: 'Privacy Notice',
    and: 'and',
    termsOfUse: 'Terms of Use',
    submit: 'Create account',
    pending: 'One moment…',
  },

  /** `/Cancellation` — reached from Cancel on the confirmation page and the profile. */
  cancellation: {
    eyebrow: 'Manage booking',
    heroLead: 'Cancel',
    heroAccent: 'booking',
    intro: 'Cancel the booking you no longer need. Other bookings in this order stay as they are.',
    bookings: 'Your booking',
    backHome: 'Back to villages',
    myBookings: 'My bookings',
    /**
     * The note after a booking is cancelled — every company's, not only the village's, so the
     * English is the app's long-standing wording. "on 03/11/2026"; the price is "50 AED".
     */
    note: (date: string) => `Your booking on ${date} has been cancelled.`,
    refundNote: (date: string, price: string) =>
      `Your booking on ${date} for ${price} is cancelled and a refund is on the way to original mode of payment, please allow up to 14 days for the refund to be processed.`,
  },
  /** The full-screen loader shown until the company is fetched */
  loader: {
    label: 'Loading the Fitness Villages',
  },
};

export type FitnessVillageCopy = typeof FITNESS_VILLAGE_COPY_EN;
